import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Markdown } from "./Markdown";
import { Save, Eye, EyeOff, ExternalLink, Scale, ClipboardCheck } from "lucide-react";

export const AdminLegalTab = () => {
  const [pages, setPages] = useState([]);
  const [slug, setSlug] = useState(null);
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [view, setView] = useState("docs");
  const [consents, setConsents] = useState(null);
  const [search, setSearch] = useState("");
  const [consentMeta, setConsentMeta] = useState({ total_users: 0, with_versioned_proof: 0 });

  const loadList = () => api.get("/legal").then((r) => setPages(r.data.pages || []));

  const loadConsents = (term = "") =>
    api
      .get("/admin/consents", { params: term ? { search: term } : {} })
      .then((r) => {
        setConsents(r.data.consents || []);
        setConsentMeta({ total_users: r.data.total_users, with_versioned_proof: r.data.with_versioned_proof });
      })
      .catch((e) => toast.error(formatErr(e)));

  useEffect(() => {
    loadList().catch((e) => toast.error(formatErr(e)));
  }, []);

  useEffect(() => {
    if (view === "consents" && consents === null) loadConsents();
  }, [view, consents]);

  useEffect(() => {
    if (!slug) return;
    api.get(`/legal/${slug}`).then((r) => setPage(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [slug]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/legal/${slug}`, {
        title: page.title,
        summary: page.summary,
        content: page.content,
      });
      setPage(r.data);
      await loadList();
      toast.success(`Guardado — versão ${r.data.version}`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={view === "docs" ? "default" : "outline"} data-testid="admin-legal-view-docs"
          onClick={() => setView("docs")}>
          <Scale className="w-4 h-4 mr-1.5" /> Documentos
        </Button>
        <Button size="sm" variant={view === "consents" ? "default" : "outline"} data-testid="admin-legal-view-consents"
          onClick={() => setView("consents")}>
          <ClipboardCheck className="w-4 h-4 mr-1.5" /> Consentimentos
        </Button>
      </div>

      {view === "consents" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="admin-consents">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <p className="font-bold text-slate-900">Prova de consentimento</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {consentMeta.total_users} utilizadores · {consentMeta.with_versioned_proof} com versão dos documentos registada
              </p>
            </div>
            <div className="flex gap-2">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Procurar por nome ou email"
                data-testid="admin-consents-search" className="w-56 h-9" />
              <Button size="sm" variant="outline" onClick={() => loadConsents(search)}>Procurar</Button>
            </div>
          </div>

          {consents === null && <p className="text-sm text-slate-500">A carregar...</p>}
          {consents !== null && consents.length === 0 && (
            <p className="text-sm text-slate-500">Sem resultados.</p>
          )}
          {consents !== null && consents.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-4 font-bold">Utilizador</th>
                    <th className="py-2 pr-4 font-bold">Tipo</th>
                    <th className="py-2 pr-4 font-bold">Aceitou em</th>
                    <th className="py-2 pr-4 font-bold">Versões</th>
                    <th className="py-2 pr-4 font-bold">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {consents.map((c) => (
                    <tr key={c.id} className="border-b border-slate-50">
                      <td className="py-2.5 pr-4">
                        <span className="font-medium text-slate-900 block">{c.name || "—"}</span>
                        <span className="text-xs text-slate-500">{c.email}</span>
                      </td>
                      <td className="py-2.5 pr-4">
                        <Badge variant="secondary">
                          {c.role === "candidate" ? "Candidato" : c.role === "company" ? "Empresa" : "Admin"}
                        </Badge>
                      </td>
                      <td className="py-2.5 pr-4 text-slate-600">
                        {c.accepted_at ? new Date(c.accepted_at).toLocaleString("pt-PT") : "—"}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-600 whitespace-nowrap">
                        {c.terms_version
                          ? `Termos v${c.terms_version}${c.privacy_version ? ` · Privacidade v${c.privacy_version}` : ""}`
                          : "— (antes do versionamento)"}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-500 text-xs">{c.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === "docs" && (
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 h-fit">
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 px-2 mb-3">
          Documentos legais
        </p>
        <div className="flex flex-col gap-1">
          {pages.map((p) => (
            <button
              key={p.slug}
              type="button"
              data-testid={`admin-legal-item-${p.slug}`}
              onClick={() => { setSlug(p.slug); setPreview(false); }}
              className={`text-left rounded-xl px-3 py-2.5 text-sm transition-colors ${
                slug === p.slug ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <span className="font-medium">{p.title}</span>
              <span className={`block text-xs mt-0.5 ${slug === p.slug ? "text-slate-400" : "text-slate-400"}`}>
                /legal/{p.slug} · v{p.version}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div>
        {!page && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
            <Scale className="w-8 h-8 mx-auto text-slate-300 mb-3" />
            <p className="text-sm">Escolhe um documento à esquerda para editar o seu conteúdo.</p>
          </div>
        )}
        {page && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">v{page.version}</Badge>
                <span className="text-xs text-slate-500">
                  Atualizado: {page.updated_at ? new Date(page.updated_at).toLocaleString("pt-PT") : "—"}
                </span>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPreview((v) => !v)} data-testid="admin-legal-preview">
                  {preview ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}
                  {preview ? "Editar" : "Pré-visualizar"}
                </Button>
                <a href={`/legal/${page.slug}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm">
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Ver página
                  </Button>
                </a>
                <Button size="sm" onClick={save} disabled={saving} data-testid="admin-legal-save">
                  <Save className="w-4 h-4 mr-1.5" /> {saving ? "A guardar..." : "Guardar"}
                </Button>
              </div>
            </div>

            {preview ? (
              <div className="border border-slate-100 rounded-xl p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">{page.title}</h2>
                <p className="text-sm text-slate-500 mb-6">{page.summary}</p>
                <Markdown content={page.content} />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Título</label>
                  <Input
                    value={page.title || ""}
                    onChange={(e) => setPage({ ...page, title: e.target.value })}
                    data-testid="admin-legal-title"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Resumo</label>
                  <Input
                    value={page.summary || ""}
                    onChange={(e) => setPage({ ...page, summary: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Conteúdo (markdown: ## título, ### subtítulo, - lista, **negrito**)
                  </label>
                  <Textarea
                    value={page.content || ""}
                    onChange={(e) => setPage({ ...page, content: e.target.value })}
                    rows={22}
                    data-testid="admin-legal-content"
                    className="mt-1 font-mono text-xs leading-relaxed"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
      )}
    </div>
  );
};
